import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { StatsRow, StatCard, Alert, Section } from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { RefreshCw, Download, Search, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { GroupAdminDialog } from "../../components/superadmin/GroupAdminDialog";

const ROLE_LABEL = {
  super_admin: "Super admin",
  group_admin: "Admin di gruppo",
  admin: "Admin",
  operator: "Operatore",
};

const fmtDate = (iso) => {
  if (!iso) return "Mai";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RoleBadge = ({ role }) => (
  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${
    role === "super_admin" ? "bg-brand/10 text-text-primary"
    : role === "group_admin" ? "bg-warning-subtle text-warning-strong"
    : role === "admin" ? "bg-accent-subtle text-accent-strong"
    : "bg-subtle text-text-secondary"
  }`}>
    {ROLE_LABEL[role] || role}
  </span>
);

export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState("all");
  const [role, setRole] = useState("all");
  const [search, setSearch] = useState("");
  const [roleUser, setRoleUser] = useState(null);

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (orgId !== "all") params.org_id = orgId;
      if (role !== "all") params.role = role;
      const { data } = await api.get("/superadmin/users", { params });
      setUsers(data);
      setFailed(false);
    } catch { setFailed(true); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/organizations");
        setOrgs(data);
      } catch {}
    })();
  }, []);

  useEffect(() => { load(); }, [orgId, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgsById = useMemo(() => {
    const m = {}; orgs.forEach((o) => m[o.id] = o.name); return m;
  }, [orgs]);

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.full_name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const kpis = useMemo(() => ({
    total: filtered.length,
    active: filtered.filter((u) => u.active).length,
    admins: filtered.filter((u) => u.role === "admin" || u.role === "super_admin").length,
    locked: filtered.filter((u) => u.locked_until && new Date(u.locked_until) > new Date()).length,
  }), [filtered]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const rows = [
      ["Email", "Nome", "Ruolo", "Organizzazione", "Status", "Ultimo accesso", "Cambio pwd richiesto"],
      ...filtered.map((u) => [
        u.email, u.full_name || "", ROLE_LABEL[u.role] || u.role,
        orgsById[u.org_id] || u.org_id, u.active ? "Attivo" : "Disattivato",
        u.last_login || "Mai", u.must_change_password ? "SI" : "NO",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tutti-utenti.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">Wattline · Super admin</div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Tutti gli utenti</h1>
          <p className="text-base text-text-secondary mt-2">Vista cross-org degli utenti della piattaforma</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10 rounded-md border-border" data-testid="sa-users-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Aggiorna
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}
                  className="h-10 rounded-md border-border" data-testid="sa-users-export">
            <Download size={14} className="mr-1.5" /> Esporta CSV
          </Button>
        </div>
      </div>

      <StatsRow columns={4}>
        <StatCard label="Utenti" value={kpis.total.toLocaleString("it-IT")} detail="Nel filtro" testId="sa-users-kpi-total" />
        <StatCard label="Attivi" value={kpis.active.toLocaleString("it-IT")}
                  detail={`${kpis.total ? Math.round((kpis.active / kpis.total) * 100) : 0}%`}
                  detailColor="success" testId="sa-users-kpi-active" />
        <StatCard label="Admin" value={kpis.admins.toLocaleString("it-IT")} detail="Admin + super admin" testId="sa-users-kpi-admins" />
        <StatCard label="Account bloccati" value={kpis.locked.toLocaleString("it-IT")}
                  detail="Lockout temporanei" detailColor={kpis.locked > 0 ? "danger" : "default"}
                  testId="sa-users-kpi-locked" />
      </StatsRow>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 max-w-md relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Cerca per email o nome…"
                 className="pl-9 h-10 rounded-md border-border bg-surface" data-testid="sa-users-search" />
        </div>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="w-52 h-10 rounded-md border-border bg-surface" data-testid="sa-users-org-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le org.</SelectItem>
            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-44 h-10 rounded-md border-border bg-surface" data-testid="sa-users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            <SelectItem value="super_admin">Super admin</SelectItem>
            <SelectItem value="group_admin">Admin di gruppo</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="operator">Operatore</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="sa-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-text-secondary text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Organizzazione</th>
                <th className="text-left px-5 py-3 font-medium">Ruolo</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Ultimo accesso</th>
                <th className="text-right px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-text-muted">Caricamento…</td></tr>
              ) : failed ? (
                <tr><td colSpan={7}><LoadErrorBox what="gli utenti" onRetry={load} testId="sa-users-error" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-text-muted">Nessun utente trovato</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.id} className="border-t border-border hover:bg-subtle" data-testid={`sa-user-row-${u.email}`}>
                  <td className="px-5 py-3 font-mono text-xs text-text-primary">{u.email}</td>
                  <td className="px-5 py-3 text-text-secondary">{u.full_name || "—"}</td>
                  <td className="px-5 py-3">
                    <Link to={`/superadmin/organizations/${u.org_id}`} className="text-accent hover:underline">
                      {orgsById[u.org_id] || u.org_id}
                    </Link>
                  </td>
                  <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3">
                    {u.active
                      ? <span className="text-xs px-2 py-0.5 rounded-md bg-success-subtle text-success-strong">Attivo</span>
                      : <span className="text-xs px-2 py-0.5 rounded-md bg-subtle text-text-muted">Disattivato</span>}
                    {u.must_change_password && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-md bg-warning-subtle text-warning-strong">PWD da cambiare</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-text-secondary tabular-nums">{fmtDate(u.last_login)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setRoleUser(u)}
                      className="inline-flex items-center gap-1 text-xs text-accent hover:underline mr-3"
                      data-testid={`sa-user-role-${u.email}`}
                    >
                      <ShieldCheck size={11} /> Ruolo
                    </button>
                    <Link to={`/superadmin/organizations/${u.org_id}`}
                          className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                          data-testid={`sa-user-manage-${u.email}`}>
                      Gestisci <ExternalLink size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Section>
        <Alert variant="info">
          Per <strong>creare, modificare, resettare password</strong> o disattivare utenti di una specifica organizzazione, apri la sua scheda
          (link &quot;Gestisci&quot;). Con <strong>Ruolo</strong> assegni l&apos;amministratore di gruppo (accesso multi-organizzazione) o
          ripristini l&apos;organizzazione di origine di un utente. I super admin possono esistere solo nell&apos;org <code className="text-xs px-1 bg-subtle rounded">8energy</code>.
        </Alert>
      </Section>

      {roleUser && (
        <GroupAdminDialog
          user={roleUser}
          orgs={orgs}
          onClose={() => setRoleUser(null)}
          onSaved={() => { setRoleUser(null); load(); }}
        />
      )}
    </div>
  );
}
